import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import {
  trackGrowthEngineActivity,
  GROWTH_ENGINE_ACTIVITY_TYPES,
} from "../api/growthEngine/progress.api";
import { getCreditDetails } from "../redux/actions/billing";
import { notifyRewardCompletions } from "../redux/actions/backgroundProcesses";

export const useGrowthEngineActivityTracker = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const invalidateRewards = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["rewards"] });
  }, [queryClient]);

  const applyMilestonesToRewardsCache = useCallback(
    (milestones) => {
      queryClient.setQueryData(["rewards"], (oldData) => {
        if (!oldData) return oldData;
        const modules = Array.isArray(oldData) ? oldData : oldData.data;
        if (!Array.isArray(modules)) return oldData;

        const updatedModules = modules.map((module) => {
          let newlyCompleted = 0;
          const updatedRewards = (module.rewards || []).map((reward) => {
            if (reward.isCompleted) return reward;

            const milestone = milestones.find((m) => {
              const normalize = (s) =>
                String(s || "").toLowerCase().replace(/[\s_-]/g, "");

              const milestoneActivityType =
                m.activityType || m.unlockCondition?.activityType || m;
              const milestoneTitle = m.title || m.activityType || "";

              if (
                milestoneActivityType &&
                normalize(reward.unlockCondition?.activityType) ===
                  normalize(milestoneActivityType)
              ) {
                return true;
              }
              if (
                milestoneTitle &&
                reward.title &&
                normalize(reward.title).includes(normalize(milestoneTitle))
              ) {
                return true;
              }
              return false;
            });

            if (milestone) {
              newlyCompleted += 1;
              return { ...reward, isCompleted: true, isUnlocked: true };
            }
            return reward;
          });

          return newlyCompleted > 0
            ? {
                ...module,
                rewards: updatedRewards,
                completedRewards: (module.completedRewards || 0) + newlyCompleted,
              }
            : module;
        });

        return Array.isArray(oldData)
          ? updatedModules
          : { ...oldData, data: updatedModules };
      });
    },
    [queryClient]
  );

  const track = useCallback(
    async (activityType, metadata = {}) => {
      try {
        const result = await trackGrowthEngineActivity(activityType, metadata);
        const backendMilestones = result?.milestonesReached || [];

        if (backendMilestones.length > 0) {
          applyMilestonesToRewardsCache(backendMilestones);
          dispatch(notifyRewardCompletions(backendMilestones));
          // A reward just completed server-side, which may have credited AI
          // credits — refresh the sidebar balance immediately.
          dispatch(getCreditDetails());
        }
        invalidateRewards();

        return result;
      } catch (error) {
        // Fail silently — activity tracking should not block the user experience.
        console.error("[GrowthEngine activity] tracking failed:", activityType, metadata, error);
        return { isFirstVisit: false, visitCount: 0, milestonesReached: [] };
      }
    },
    [applyMilestonesToRewardsCache, invalidateRewards, dispatch]
  );

  const trackCampaignLaunched = useCallback(
    (metadata = {}) => track(GROWTH_ENGINE_ACTIVITY_TYPES.LAUNCH_CAMPAIGN, metadata),
    [track]
  );

  const trackCampaignHistoryVisited = useCallback(
    (metadata = {}) => track(GROWTH_ENGINE_ACTIVITY_TYPES.VISIT_CAMPAIGN_HISTORY, metadata),
    [track]
  );

  const trackCampaignEdited = useCallback(
    (metadata = {}) => track(GROWTH_ENGINE_ACTIVITY_TYPES.EDIT_CAMPAIGN, metadata),
    [track]
  );

  const trackCampaignEditorVisited = useCallback(
    (metadata = {}) => track(GROWTH_ENGINE_ACTIVITY_TYPES.VISIT_CAMPAIGN_EDITOR, metadata),
    [track]
  );

  return {
    track,
    trackCampaignLaunched,
    trackCampaignHistoryVisited,
    trackCampaignEdited,
    trackCampaignEditorVisited,
    GROWTH_ENGINE_ACTIVITY_TYPES,
  };
};

export { GROWTH_ENGINE_ACTIVITY_TYPES };
